import { useEffect, useRef } from 'react';
import { invalidateTags } from '@/shared/query/registry';
import { eventStreamUrl } from './api';
import { Backoff } from './backoff';
import { publishEnvelope } from '@/shared/realtime/bus';
import { isSessionEnded } from './controlFrames';
import {
  noteError,
  noteFrame,
  setBrowserOnline,
  setStatus,
  useStreamHealth,
  type StreamHealth,
} from '@/shared/ui/streamHealth';
import { parseEnvelope, isKnownFrame, EVENT_FRAMES } from './types';
import { ALL_DATA_TAGS, TagCoalescer } from './invalidations';
export interface UseEventInvalidationsOptions {
  enabled?: boolean;
  createSource?: (url: string) => EventSource;
  /**
   * **会话没了**（服务端发来 `stream.reconnect` + 白名单 reason）时的回调。
   *
   * ⚠️ **为什么需要它**：`EventSource` 看不到 HTTP 状态码 ⇒ 只能靠服务端
   * 主动发控制帧说一声（**为什么**见 `features/events/controlFrames.ts` 的
   * 模块导语；服务端在撤销 / 禁用 / 软删之后会先写这个控制帧再收流，
   * ADR 0007 D2）。
   *
   * ⚠️ **回调身份不进依赖**：它进依赖 ⇒ 每次父组件重渲染都会重建 EventSource
   * ⇒ 连接被反复掐断。⇒ 内部用 ref 持有最新的一份。
   */
  onSessionEnded?: () => void;
}
export function useEventInvalidations(options: UseEventInvalidationsOptions = {}): StreamHealth {
  const { enabled = true, createSource } = options;
  // 回调的最新一份（不进 effect 依赖 ⇒ 不会因身份变化重建连接）。
  const onSessionEndedRef = useRef(options.onSessionEnded);
  useEffect(() => {
    onSessionEndedRef.current = options.onSessionEnded;
  });
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;
    const backoff = new Backoff();
    const coalescer = new TagCoalescer((tags) => invalidateTags(tags));
    const open = createSource ?? ((url: string) => new EventSource(url));
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;
    // 「会话已经没了」——**终态**：不再重连（重连只会拿到 401 白转）。
    let ended = false;
    const onFrame = (frameName: string, event: MessageEvent): void => {
      noteFrame();
      const envelope = parseEnvelope(event.data, frameName);
      if (envelope === null) return;
      publishEnvelope(envelope);
      if (isSessionEnded(envelope)) {
        // 会话没了 ⇒ 停在这条：不再重连，也不把这次连接当故障。
        ended = true;
        setStatus('offline');
        onSessionEndedRef.current?.();
        return;
      }
      if (!isKnownFrame(envelope.type)) {
        console.warn('[sse] 未收录的帧，不触发失效：', envelope.type);
        return;
      }
      coalescer.push(envelope.type);
    };
    const frameListeners = EVENT_FRAMES.map(
      (frame) =>
        [
          frame,
          ((event: Event) => onFrame(frame, event as MessageEvent)) as EventListener,
        ] as const,
    );
    const bareListener = ((event: Event): void =>
      onFrame('', event as MessageEvent)) as EventListener;
    const closeSource = (): void => {
      if (source !== null) {
        source.onopen = null;
        source.onmessage = null;
        source.onerror = null;
        for (const [frame, listener] of frameListeners) source.removeEventListener(frame, listener);
        source.removeEventListener('message', bareListener);
        source.close();
        source = null;
      }
    };
    const scheduleRetry = (): void => {
      if (disposed || ended) return;
      const wait = backoff.next();
      setStatus('retrying');
      timer = setTimeout(() => {
        void connect();
      }, wait);
    };
    const connect = (): void => {
      if (disposed || ended) return;
      setStatus('connecting');
      closeSource();
      const src = open(eventStreamUrl());
      source = src;
      src.onopen = () => {
        backoff.reset();
        setStatus('open');
        invalidateTags(ALL_DATA_TAGS);
      };
      for (const [frame, listener] of frameListeners) {
        src.addEventListener(frame, listener);
      }
      src.addEventListener('message', bareListener);
      src.onerror = () => {
        closeSource();
        // ⚠️ **会话已经没了** ⇒ 这不是「故障」⇒ 状态保持 'offline'，也不重连
        // （重连只会拿到 401 白转）。控制帧那条路径已经把它标成终态了。
        if (ended) return;
        noteError('stream error');
        scheduleRetry();
      };
    };
    const onOnline = (): void => {
      setBrowserOnline(true);
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      backoff.reset();
      connect();
    };
    const onOffline = (): void => setBrowserOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    connect();
    return () => {
      disposed = true;
      if (timer !== null) clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      closeSource();
    };
  }, [enabled, createSource]);
  return useStreamHealth();
}
